import { makeStyles, Snackbar } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import React, { ReactElement, useContext, useEffect, useState } from 'react';
import classNames from 'classnames';
import { AppContext } from 'context';
import WarningIcon from './Icons/WarningIcon';

const useStyles = makeStyles({
  notifyInfo: {
    marginTop: '100px',

    '& > div': {
      boxShadow: '-8px 8px 8px rgba(189, 189, 189, 0.3)',
      backgroundColor: '#FFFFFF',
      borderColor: '#CFD4DA',
      borderRadius: '8px',

      '& .MuiAlert-icon': {
        paddingTop: '16px',

        '& svg': {
          fill: '#0F626F',
          width: '20px',
          height: '20px',
        },
      },
      '& .MuiAlert-message': {
        fontSize: '14px',
        lineHeight: '20px',
        paddingTop: '16px',
        paddingBottom: '16px',

        '& a': {
          color: '#0F626F',
          textDecoration: 'none',
          lineHeight: '20px',
        },
      },
      '& .MuiAlert-action svg': {
        fill: '#2F3741',
        width: '14px',
        height: '14px',
      },
    },
  },
  small: {
    width: '17%',
  },
  hiddenCloseButton: {
    '& .MuiAlert-action': {
      display: 'none',
    },
  },
  alert: {
    '& > div': {
      '& .MuiAlert-icon': {
        alignItems: 'center',
      },
      '& .MuiAlert-action': {
        alignItems: 'start',
      },
    },
  },
});

interface Notification {
  severity: 'success' | 'info' | 'warning' | 'error';
  message: string | ReactElement;
  width?: 'small';
  hiddenCloseButton?: boolean;
}

let openFn: (newNotification: Notification | null, autoClose: boolean) => void;

const Notifier = () => {
  const classes = useStyles();
  const [notification, setNotification] = useState<Notification | null>(null);
  const [autoClose, setAutoClose] = useState<number | null>(6000);
  const [open, setOpen] = useState(false);
  const { user } = useContext(AppContext);

  const doOpen = (newNotification: Notification | null, autoClose: boolean) => {
    setNotification(newNotification);
    setOpen(true);
    !autoClose ? setAutoClose(null) : setAutoClose(6000);
  };

  useEffect(() => {
    openFn = doOpen;
  }, []);

  useEffect(() => {
    if (!user) {
      setOpen(false);
    }
  }, [user]);

  const handleClose = (event?: React.SyntheticEvent, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }

    setOpen(false);
  };

  if (!notification) {
    return null;
  }

  return (
    <Snackbar
      className={classNames({
        [classes.alert]: notification.severity !== 'success',
        [classes.notifyInfo]: notification.severity === 'info',
        // @ts-ignore
        [classes[notification.width]]: notification.width,
        [classes.hiddenCloseButton]: notification.hiddenCloseButton,
      })}
      open={open}
      anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      autoHideDuration={autoClose}
      onClose={handleClose}
      data-qa-id={notification.severity + '_notification'}
    >
      <Alert
        onClose={handleClose}
        {...(notification.severity === 'warning' && { icon: <WarningIcon /> })}
        severity={notification.severity}
        variant={notification.severity === 'info' ? 'outlined' : 'filled'}
      >
        {notification.message}
      </Alert>
    </Snackbar>
  );
};

export const notify = (notification: Notification | null, autoClose: boolean = true) => {
  if (openFn) {
    openFn(notification, autoClose);
  }
};

export default Notifier;
